import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * HelpDialog - Overlay with deck builder instructions
 */
export default function HelpDialog({ open, onOpenChange }) {
  const { t } = useLanguage();

  const sections = [
    {
      title: t('help.search.title', 'Search & Add Cards'),
      body: t('help.search.body', 'Use the Search pane to find cards by name. Apply filters for card type, level, rarity, and more. Click a card to add it to your deck.'),
    },
    {
      title: t('help.deckLayout.title', 'Deck Layout'),
      body: t('help.deckLayout.body', 'Your deck is displayed in the left pane. Hover over a card to adjust its count or remove it. The card count badge shows how many copies are in the deck.'),
    },
    {
      title: t('help.exportImport.title', 'Export & Import'),
      body: t('help.exportImport.body', 'Export creates a shareable code or JSON of your deck. Import lets you paste a code or JSON to restore a deck. Use Share to copy a direct link.'),
    },
    {
      title: t('help.deckImage.title', 'Generate Deck Image'),
      body: t('help.deckImage.body', 'Creates a visual image of your entire deck that you can save or share.'),
    },
    {
      title: t('help.tts.title', 'Export to TTS'),
      body: t('help.tts.body', 'Downloads a .zip file for Tabletop Simulator. Extract the contents into your TTS Saved Objects folder.'),
    },
    {
      title: t('help.tts.pathLabel', 'TTS Path'),
      body: t('help.tts.pathExample', 'Documents\\My Games\\Tabletop Simulator\\Saves\\Saved Objects'),
      isMono: true,
    },
    {
      title: t('help.savedDecks.title', 'Saved Decks'),
      body: t('help.savedDecks.body', 'Save your deck locally to load it later. Decks are stored in your browser.'),
    },
    {
      title: t('help.preview.title', 'Card Preview'),
      body: t('help.preview.body', 'Hover over any card in the deck or search results to see an enlarged preview in the right pane.'),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {t('help.title', 'Deck Builder Help')}
          </DialogTitle>
          <DialogDescription>
            {t('help.description', 'How to build and manage your deck.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {sections.map((s, i) => (
            <div key={i} className="space-y-1">
              <h4 className="text-sm font-semibold">{s.title}</h4>
              <p className={`text-sm text-muted-foreground ${s.isMono ? 'font-mono bg-muted p-2 rounded text-xs' : ''}`}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
